import React from "react";
import Link from "next/link";
import Select from "react-select";
import dynamic from "next/dynamic";
import useFetcher from "@hooks/fetcher";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useSession } from "next-auth/react";
import ProfileDropdown from "./ProfileDropdown";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import BusinessIcon from "@mui/icons-material/Business";
import CustomDropdown from "../../components/ui/CustomDropdown";
import usePersistedLanguage from "@hooks/usePersistedLanguage";
import useLeaveImpersonation from "@hooks/useLeaveImpersonation";

// Novu SDK loaded lazily, client-only. Its ESM build has a circular-import TDZ
// that crashed /workflows (client ReferenceError + SSR 500, incident
// 2026-07-08). With ssr:false + dynamic import the SDK never runs on the
// server and pages that hide the top bar never download the chunk at all.
// See NovuNotificationBell.js for the full explanation.
const NovuNotificationBell = dynamic(() => import("./NovuNotificationBell"), {
  ssr: false,
  loading: () => null,
});

// If the Novu chunk still fails to load/evaluate on some page, degrade to
// "no bell" instead of letting the error bubble to the app-level boundary
// (which blanks the whole screen).
class NovuBellErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("Novu notification bell failed (non-fatal):", error);
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

const TopNav = () => {
  usePersistedLanguage();

  const { data: session, update } = useSession();
  const { locales, push, pathname, asPath } = useRouter();
  const { t } = useTranslation("common");

  const getFlagEmoji = (locale) => {
    let countryCode = locale.toUpperCase();

    if (countryCode === "EN") {
      countryCode = "US";
    }
    if (countryCode === "PT") {
      countryCode = "PT";
    }

    if (countryCode.length !== 2) {
      return "";
    }

    const codePoints = countryCode
      .split("")
      .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const updateSession = async (company) => {
    company.current = true;

    await update({
      ...session,
      user: {
        ...session.user,
        currentCompany: company,
      },
    });

    window.location.reload();
  };

  const { leaveImpersonation } = useLeaveImpersonation();

  const { data } = useFetcher("/api/v1/companies/my-companies");

  React.useEffect(() => {
    function updateMargin() {
      const rightElement = document.getElementById("top-nav");
      const leftElement = document.getElementById("sidenav-main");
      const leftRect = leftElement?.getBoundingClientRect();

      if (leftRect && leftRect.right > 0 && leftRect.left < window.innerWidth) {
        const leftWidth = leftElement.clientWidth;
        rightElement.style.marginLeft = leftWidth + "px";
      } else {
        rightElement && (rightElement.style.marginLeft = "0px");
      }
    }

    updateMargin();
    window.addEventListener("resize", updateMargin);
    window.addEventListener("scroll", updateMargin);
    return () => {
      window.removeEventListener("resize", updateMargin);
      window.removeEventListener("scroll", updateMargin);
    };
  }, []);

  const avatarColors = [
    "bota-dark-cyan-lime-green",
    "bota-strong-cyan-lime-green",
    "bota-light-blue",
    "bota-purple",
    "bota-cyan",
    "bota-magenta",
    "dark",
    "primary",
    "info",
    "warning",
  ];

  const companies = data?.companies?.map((company) => {
    const randomColor =
      avatarColors[Math.floor(Math.random() * avatarColors.length)];

    const initials =
      company?.name.charAt(0).toUpperCase() +
      company?.name.split(" ").slice(-1).pop().charAt(0).toUpperCase();

    return {
      label: (
        <div className="d-flex align-items-center">
          <span
            className={`avatar avatar-sm rounded-circle bg-${randomColor} tw-text-white d-flex align-items-center justify-content-center tw-w-6 tw-h-6 tw-text-xs tw-font-bold`}
          >
            {initials}
          </span>
          <span className="ms-3 tw-text-sm tw-font-medium">
            {company?.name}
          </span>
          {session.user?.currentCompany?.id == company.id && (
            <CheckIcon className="ms-2 tw-text-green-500 tw-w-4 tw-h-4" />
          )}
        </div>
      ),
      value: company.id,
    };
  });

  const CompanySwitcher = () => {
    if (companies && companies.length >= 1) {
      return (
        <li className="dropdown me-3 tw-min-w-[180px]">
          <Select
            value={companies?.filter(
              (c) => c.value == session.user?.currentCompany?.id
            )}
            onChange={(val) =>
              updateSession(data?.companies?.find((c) => c.id == val.value))
            }
            options={companies}
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: "0.5rem",
                minHeight: "40px",
                borderColor: "#E5E7EB",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#9CA3AF",
                },
              }),
            }}
          />
        </li>
      );
    }

    return (
      <li className="dropdown me-3 tw-flex items-center">
        <Link
          href="/companies"
          className="tw-flex items-center text-bota-light-blue tw-text-sm tw-font-medium"
          passHref
        >
          <BusinessIcon className="tw-w-5 tw-h-5" />
          <span className="ms-2">{t("top-nav.create-a-company")}</span>
        </Link>
      </li>
    );
  };

  const LANGUAGE_NAMES = {
    en: "English",
    pt: "Português",
    es: "Español",
    fr: "Français",
  };

  const LanguageSwitcher = () => (
    <li className="me-3 tw-list-none">
      <CustomDropdown
        alignRight={true}
        trigger={
          <div
            className="tw-p-2 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors"
            style={{
              color: "var(--bs-bota-strong-cyan-lime-green)",
            }}
          >
            <LanguageIcon className="tw-w-6 tw-h-6" />
          </div>
        }
        menu={locales.map((locale) => (
          <li
            key={locale}
            onClick={() => {
              push(pathname, asPath, { locale });
              localStorage.setItem("BOTATENDE_LOCALE", locale);
            }}
          >
            <a
              className="tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-md tw-w-full"
              href="#"
            >
              <span className="d-flex align-items-center">
                <span className="me-3 tw-text-lg">{getFlagEmoji(locale)}</span>
                <span className="ms-3 tw-text-sm">
                  {LANGUAGE_NAMES[locale] || locale}
                </span>
              </span>
            </a>
          </li>
        ))}
      />
    </li>
  );

  const NotificationBellComponent = () => (
    <li className="mx-2 tw-list-none">
      <NovuBellErrorBoundary>
        <NovuNotificationBell
          subscriberId={session?.user?.subscriberId}
          subscriberHash={session?.user?.subscriberHash}
        />
      </NovuBellErrorBoundary>
    </li>
  );

  return (
    <>
      <nav
        className="navbar navbar-top navbar-main navbar-expand-lg sticky-top px-0 tw-shadow-md tw-bg-white"
        data-scroll="true"
        style={{ zIndex: 99, marginLeft: "0px !important" }}
        id="top-nav"
      >
        <div className="container-fluid my-2 d-flex justify-content-between align-items-center">
          <div></div>

          <ul className="navbar-nav d-flex flex-row align-items-center tw-space-x-2">
            {session && session?.admin_user && (
              <li className="mx-2 tw-list-none">
                <button
                  className="btn bg-bota-light-blue tw-text-white tw-text-sm tw-font-medium hover:tw-bg-bota-purple/80"
                  type="button"
                  onClick={leaveImpersonation}
                >
                  {t("buttons:leave-impersonation")}
                </button>
              </li>
            )}

            <CompanySwitcher />

            <LanguageSwitcher />

            <NotificationBellComponent />

            <li className="tw-list-none">
              <ProfileDropdown session={session} t={t} />
            </li>
          </ul>
        </div>
      </nav>

      {session?.user?.currentCompany?.require_whatsapp &&
        !session?.user?.whatsapp_number && (
          <div className="tw-bg-orange-500 tw-text-white tw-py-2 tw-text-sm tw-font-medium">
            <div className="container-fluid">
              <div className="row">
                <div className="col text-center">
                  {t("common:top-nav.verify-whatsapp")}
                  <Link
                    href="/profile"
                    className="text-decoration-none tw-ml-2"
                  >
                    <span className="text-bota-light-blue tw-underline tw-font-bold">
                      {t("common:top-nav.verify-now")}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

      {session?.user?.currentCompany?.subscription &&
        session?.user?.currentCompany?.subscription?.status !== "ACTIVE" &&
        session?.user?.currentCompany?.subscription?.status !== "TRIALING" && (
          <div className="tw-bg-red-500 tw-text-white tw-py-2 tw-text-sm tw-font-medium">
            <div className="container-fluid">
              <div className="row">
                <div className="col text-center">
                  {session?.user?.currentCompany?.subscription?.status ===
                  "CANCELED"
                    ? t("common:top-nav.canceled-subscription")
                    : session?.user?.currentCompany?.subscription?.status ===
                      "OVERDUE"
                    ? t("common:top-nav.overdue-subscription")
                    : session?.user?.currentCompany?.subscription?.status ===
                      "UNPAID"
                    ? t("common:top-nav.unpaid-invoice")
                    : null}

                  <Link
                    href={`/subscriptions/invoice/${session?.user?.currentCompany?.subscription?.invoice?.id}`}
                    className="text-decoration-none text-dark"
                  >
                    <span className="text-bota-light-blue tw-underline tw-font-bold tw-uppercase">
                      {session?.user?.currentCompany?.subscription?.status ===
                      "CANCELED"
                        ? t("buttons:renew")
                        : session?.user?.currentCompany?.subscription
                            ?.status === "PAST_DUE" ||
                          session?.user?.currentCompany?.subscription
                            ?.status === "UNPAID"
                        ? t("buttons:pay-now")
                        : t("buttons:renew")}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default TopNav;
